import "./bookingCalendar.css"
import { useAppSelector } from "@/store"
import { hasAnyPermission, hasPermission } from "@/store/permissionsSlice"
import TeamDayPage from "./managerDashboard/page"
import StaffWeekPage from "./staffWeek/page"

/**
 * The staff appointment calendar, which is two pages behind one route.
 *
 * `appointments:manage` administers the schedule, so it gets the office: one team's day, a column
 * per member, every appointment at the site — and, on picking one of those members, that person's
 * week on its own. `appointments:view` alone belongs to someone who takes appointments rather than
 * arranges them, so it gets their own week and nobody else's — narrowed by the server, not by this
 * component, because the appointments it leaves out carry attendee names and phone numbers.
 *
 * The permission check here is the page's, not the API's: the week of a named colleague comes from
 * an endpoint that requires `appointments:manage` of its own, so a client that rendered it anyway
 * would be refused rather than obeyed.
 */
export default function AppointmentsPage() {
  const { permissions } = useAppSelector(state => state.permissions)
  const { viewingStaffCalUserId } = useAppSelector(state => state.bookingCalendar)

  const canView = hasAnyPermission(permissions, ["appointments:view", "appointments:manage"])
  const canManage = hasPermission(permissions, "appointments:manage")

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center py-16 text-sm text-muted-foreground">
        You don't have access to appointments.
      </div>
    )
  }

  if (!canManage) {
    return <StaffWeekPage />
  }

  return viewingStaffCalUserId == null
    ? <TeamDayPage />
    : <StaffWeekPage calUserId={viewingStaffCalUserId} />
}
